import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {filter, map, Observable, switchMap} from 'rxjs';
import {
  IBankInfo,
  IBankInfoAgreement,
  IBankInfoCards,
  IBankInfoCredential,
  IBankInfoPUT,
} from 'src/app/interfaces/accountant.interfaces';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {AccessManagementService} from 'src/app/services/access-management.service';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {DialogService} from 'src/app/services/dialog.service';
import {EmployeeService} from 'src/app/services/employee.service';
import {
  columnTypes,
  ITableRowAction,
  UITableHeader,
} from 'src/app/shared/ui-kits/table/table.models';
import {roles} from 'src/app/shared/utils/roles';
import {Utils} from 'src/app/shared/utils/utils';
import {AgreementPopupComponent} from './agreement-popup/agreement-popup.component';
import {AutentificationCredentialsPopupComponent} from './autentification-credentials-popup/autentification-credentials-popup.component';
import {CardNumberPopupComponent} from './card-number-popup/card-number-popup.component';
import {NewBankPopupComponent} from './new-bank-popup/new-bank-popup.component';
import {ToastrService} from 'ngx-toastr';

@UntilDestroy()
@Component({
  selector: 'app-bank-info',
  templateUrl: './bank-info.component.html',
  styleUrls: ['./bank-info.component.scss'],
})
export class BankInfoComponent implements OnInit {
  public employeeId!: number;
  public hasPE!: Observable<boolean>;
  public bankInfoForm!: UntypedFormGroup;
  public hasAnyBankInfo!: boolean;
  public isEditable = false;
  public bankInfoId!: number;
  public idOfPE!: number;
  public availableBanksDropdown: IDropdownObject[] = [];
  public agreementDropdown: IDropdownObject[] = [];
  public isLoaded = false;
  public formValueDefault!: IBankInfoPUT;
  public changed = false;
  public isCEO: boolean = false;
  public isAccountant: boolean = false;
  public ableToEdit: boolean = false;

  @ViewChild('saveButton') saveButton!: ElementRef;

  /**
   * Headers and array for the card numbers table
   */
  public cardsTableData: IBankInfoCards[] = [];
  public cardsTableHeaders: UITableHeader[] = [
    {key: 'cardNumber', index: 0},
    {key: 'endDate', index: 1, type: columnTypes.datePicker},
    {key: 'bankInfoId', index: 2, notVisible: true},
    {key: 'id', index: 3, notVisible: true},
  ];
  /**
   * Headers and array for the bank autentification credentials table
   */
  public autentificationTableData: IBankInfoCredential[] = [];
  public autentificationTableHeaders: UITableHeader[] = [
    {key: 'login', index: 0},
    {key: 'passwordFilePath', index: 1},
    {key: 'keyFilePath', index: 2},
    {key: 'backupPasswordFilePath', index: 3},
    {key: 'backupKeyFilePath', index: 4},
    {key: 'cloudPasswordFilePath', index: 5},
    {key: 'cloudKeyFilePath', index: 6},
    {key: 'comment', index: 7},
    {key: 'bankInfoId', index: 8, notVisible: true},
    {key: 'id', index: 9, notVisible: true},
  ];
  /**
   * Headers and array for the agreement info table
   */
  public agreementTableData: IBankInfoAgreement[] = [];
  public agreementTableHeaders: UITableHeader[] = [
    {key: 'agreementNo', index: 0},
    {key: 'agreementStartDate', index: 1, type: columnTypes.datePicker},
    {key: 'agreementEndDate', index: 2, type: columnTypes.datePicker},
    {key: 'nda', index: 3, type: columnTypes.dropDown},
    {key: 'comment', index: 4},
    {key: 'bankInfoId', index: 5, notVisible: true},
    {key: 'id', index: 6, notVisible: true},
  ];
  public isEqualObjects = Utils.isEqualObjects;
  public createEmptyObjectUtils = Utils.createEmptyObject;

  constructor(
    private route: ActivatedRoute,
    private employee: EmployeeService,
    private dialog: DialogService,
    private notifier: ToastrService,
    private _fb: UntypedFormBuilder,
    private closeHandler: CloseHandlerService,
    private access: AccessManagementService
  ) {
    this.employeeId = this.route.snapshot.parent!.params['id'];
  }

  ngOnInit(): void {
    this.employee
      .isEmployeeHasPE(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe(peId => (this.idOfPE = peId));

    this.bankInfoForm = this._fb.group({
      id: new UntypedFormControl(0),
      peId: new UntypedFormControl(null),
      bankName: new UntypedFormControl(null),
      usdiban: new UntypedFormControl(null),
      uahiban: new UntypedFormControl(null),
      swiftCode: new UntypedFormControl(null),
    });
    this.isLoaded = true;

    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.isEditable = false;
      this.bankInfoForm.markAsUntouched();
      this.changed = true;
      this.bankInfoForm.patchValue(this.formValueDefault);
      this.changed = false;
    });
    this.bankInfoForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: IBankInfoPUT) => {
        if (
          value &&
          this.isEditable &&
          !this.changed &&
          !this.isEqualObjects(value, this.formValueDefault)
        ) {
          this.closeHandler.notSaved = true;
        }
      });
    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });

    this.getBankInfo()
      .pipe(
        switchMap(() => {
          return this.bankInfoForm.get('bankName')!.valueChanges.pipe(
            filter(value => value?.id && value.id !== this.bankInfoId),
            switchMap(bankName => this.getBankInfo(bankName.id))
          );
        })
      )
      .subscribe();

    this.isAccountant = this.access.checkPermissions(
      roles.ACCOUNTANT_DEPARTMENT
    );
    this.isCEO = this.access.checkPermissions(roles.CEO);
    if (this.isAccountant || this.isCEO) this.ableToEdit = true;

    setTimeout(() => (this.formValueDefault = this.bankInfoForm.value));
  }
  /**
   * Function that call the GET method for the whole bankInfo Tab
   * @param bankId id of the selected bank from dropdown
   */
  getBankInfo(bankId?: number) {
    return this.employee.getBankInfo(this.employeeId, bankId).pipe(
      untilDestroyed(this),
      filter(bankInfo => bankInfo !== null),
      switchMap(bankInfo => {
        this.hasAnyBankInfo = true;
        this.bankInfoId = bankInfo.id;
        this.bankInfoForm.patchValue(bankInfo);
        if (bankInfo.bankName) {
          const bankDropdown = {id: bankInfo.id, name: bankInfo.bankName};
          this.availableBanksDropdown = [bankDropdown];
          this.bankInfoForm.controls['bankName'].setValue(bankDropdown);
        }
        this.buildTables(bankInfo);
        this.closeHandler.notSaved = false;
        return this.employee.getBankInfoDropdowns(this.employeeId);
      }),
      map(dropdowns => {
        this.agreementDropdown = dropdowns.YesNoEnum;
        this.availableBanksDropdown = dropdowns.PEAccountData;
      })
    );
  }

  getUpdatedBankInfo(bankId?: number) {
    this.employee
      .getBankInfo(this.employeeId, bankId)
      .pipe(
        untilDestroyed(this),
        filter(bankInfo => bankInfo !== null)
      )
      .subscribe(bankInfo => this.buildTables(bankInfo));
  }
  /**
   * Function that open the newBank popup and afther the close add new bank to BE
   */
  addNewBank() {
    this.dialog
      .open(NewBankPopupComponent, {
        data: {
          peId: this.idOfPE,
          dropdown: this.agreementDropdown,
        },
        settings: {
          width: '870px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(response => this.employee.addNewBankInfo(response)),
        switchMap(newBankId => {
          this.bankInfoId = newBankId;
          return this.getBankInfo(newBankId);
        })
      )
      .subscribe();
  }
  /**
   * Function for creating an empty object from the headers
   * @param headers table headers that we are looping through them
   * @returns empty object with null value, and with current id for bankInfo and PE
   */
  createEmptyObject(headers: UITableHeader[]) {
    const emptyData = this.createEmptyObjectUtils(headers);
    emptyData.bankInfoId = this.bankInfoId;
    emptyData.peId = this.idOfPE;
    return emptyData;
  }

  openCreditCards(event: ITableRowAction, headers: UITableHeader[]) {
    switch (event.action) {
      case 'add':
        this.addTheFirstCard(headers);
        break;
      case 'edit':
        this.dialog
          .open(CardNumberPopupComponent, {
            data: this.cardsTableData[event.index],
            settings: {
              width: '350px',
              height: '250px',
            },
          })
          .afterClosed()
          .pipe(
            untilDestroyed(this),
            filter(data => !!data)
          )
          .subscribe(res => {
            if (res === 'delete') {
              this.getUpdatedBankInfo(this.bankInfoId);
              return;
            }
            this.employee
              .updateCardNumber(res)
              .pipe(untilDestroyed(this))
              .subscribe(() => this.getUpdatedBankInfo(this.bankInfoId));
          });
        break;
      default:
        break;
    }
  }

  openCredentials(event: ITableRowAction, headers: UITableHeader[]) {
    switch (event.action) {
      case 'add':
        this.addTheFirstCredential(headers);
        break;
      case 'edit':
        this.dialog
          .open(AutentificationCredentialsPopupComponent, {
            data: this.autentificationTableData[event.index],
            settings: {
              width: '870px',
            },
          })
          .afterClosed()
          .pipe(
            untilDestroyed(this),
            filter(data => !!data)
          )
          .subscribe(res => {
            if (res === 'delete') {
              this.getUpdatedBankInfo(this.bankInfoId);
              return;
            }
            this.employee
              .updateCredential(res)
              .pipe(untilDestroyed(this))
              .subscribe(() => this.getUpdatedBankInfo(this.bankInfoId));
          });
        break;
      default:
        break;
    }
  }

  addTheFirstCard(headers: UITableHeader[]) {
    this.dialog
      .open(CardNumberPopupComponent, {
        data: this.createEmptyObject(headers),
        settings: {
          width: '350px',
          height: '250px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data)
      )
      .subscribe(res => {
        this.employee
          .addNewCard(res)
          .pipe(untilDestroyed(this))
          .subscribe(() => this.getUpdatedBankInfo(this.bankInfoId));
      });
  }

  addTheFirstCredential(headers: UITableHeader[]) {
    this.dialog
      .open(AutentificationCredentialsPopupComponent, {
        data: this.createEmptyObject(headers),
        settings: {
          width: '870px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data)
      )
      .subscribe(res => {
        this.employee
          .addNewCredential(res)
          .pipe(untilDestroyed(this))
          .subscribe(() => this.getUpdatedBankInfo(this.bankInfoId));
      });
  }

  openAgreement(event: ITableRowAction, headers: UITableHeader[]) {
    switch (event.action) {
      case 'add':
        this.addTheFirstAgreement(headers);
        break;
      case 'edit':
        this.dialog
          .open(AgreementPopupComponent, {
            data: {
              content: this.agreementTableData[event.index],
              dropdown: this.agreementDropdown,
            },
            settings: {
              width: '660px',
              height: '300px',
            },
          })
          .afterClosed()
          .pipe(
            untilDestroyed(this),
            filter(data => !!data)
          )
          .subscribe(res => {
            if (res === 'delete') {
              this.getUpdatedBankInfo(this.bankInfoId);
              return;
            }
            this.employee
              .updateAgreement(res)
              .pipe(untilDestroyed(this))
              .subscribe(() => this.getUpdatedBankInfo(this.bankInfoId));
          });
        break;
      default:
        break;
    }
  }

  addTheFirstAgreement(headers: UITableHeader[]) {
    this.dialog
      .open(AgreementPopupComponent, {
        data: {
          content: this.createEmptyObject(headers),
          dropdown: this.agreementDropdown,
        },
        settings: {
          width: '660px',
          height: '300px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data)
      )
      .subscribe(res => {
        this.employee
          .addNewAgreement(res)
          .pipe(untilDestroyed(this))
          .subscribe(() => this.getUpdatedBankInfo(this.bankInfoId));
      });
  }

  onEdit() {
    this.isEditable = !this.isEditable;
  }

  buildTables(data: IBankInfo) {
    this.cardsTableData = data.cards;
    this.agreementTableData = data.agreementInfos;
    this.autentificationTableData = data.bankCredentials;
  }

  save() {
    const updatedBankInfo = this.bankInfoForm.value;
    updatedBankInfo.bankName = updatedBankInfo.bankName.name;
    this.employee
      .updateBankInfo(updatedBankInfo)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isEditable = !this.isEditable;
        this.notifier.success('Saved');
        this.closeHandler.notSaved = false;
      });
  }
}
